// Biblioteca de medios de Marketing (imágenes para correos y landings).
// Reutilizable: <MediaPicker open onClose onSelect={(m) => …} />. Sube PNG/JPG/GIF/WEBP (máx. 5 MB)
// a /api/v1/mkt/media y devuelve la URL pública absoluta.
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Link2, Trash2, X } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Button, EmptyState, Input, Loading } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";

export interface MediaItem { id: string; filename: string; contentType: string; sizeBytes: number; createdAt: string; url: string }

const ACCEPT = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

const readAsBase64 = (f: File): Promise<string> => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).replace(/^data:[^,]*,/, ""));
  r.onerror = () => rej(new Error("No se pudo leer el archivo."));
  r.readAsDataURL(f);
});

const fmtSize = (n: number): string => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Hook de la biblioteca (lista + subida) por si otra pantalla la necesita sin el diálogo. */
export function useMediaLibrary(enabled = true) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["mkt-media"], queryFn: () => api.get<{ items: MediaItem[] }>("/mkt/media"), enabled });
  const upload = useMutation({
    mutationFn: async (f: File) => {
      if (!ACCEPT.includes(f.type)) throw new Error("Solo se admiten imágenes PNG, JPG, GIF o WEBP.");
      if (f.size > MAX_BYTES) throw new Error("La imagen supera 5 MB.");
      return api.post<MediaItem>("/mkt/media", { filename: f.name, contentType: f.type, dataBase64: await readAsBase64(f) });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["mkt-media"] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/media/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["mkt-media"] }); },
  });
  return { list, upload, remove };
}

export function MediaPicker({ open, onClose, onSelect, allowUrl = true }: {
  open: boolean; onClose: () => void; onSelect: (m: { url: string; filename?: string }) => void; allowUrl?: boolean;
}): JSX.Element | null {
  const { list, upload, remove } = useMediaLibrary(open);
  const isAdmin = Boolean(useMe().data?.admin);
  const fileRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onFiles = async (files: FileList | null): Promise<void> => {
    for (const f of Array.from(files ?? [])) {
      try { await upload.mutateAsync(f); toast.success(`«${f.name}» subida.`); } catch (e) { toast.error((e as Error).message); }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Biblioteca de medios" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card w-full max-w-4xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-line">
          <h3 className="font-semibold text-ink">Biblioteca de medios</h3>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <input ref={fileRef} type="file" accept={ACCEPT.join(",")} multiple className="hidden" onChange={(e) => { void onFiles(e.target.files); }} />
                <Button small variant="primary" disabled={upload.isPending} onClick={() => fileRef.current?.click()}>
                  <ImagePlus size={14} className="mr-1 inline" />{upload.isPending ? "Subiendo…" : "Subir imagen"}
                </Button>
              </>
            )}
            <button className="btn-ghost btn-sm" onClick={onClose} aria-label="Cerrar"><X size={16} /></button>
          </div>
        </div>
        {allowUrl && (
          <form className="flex gap-2 px-5 py-3 border-b border-line" onSubmit={(e) => {
            e.preventDefault();
            if (!/^https:\/\//i.test(url.trim())) { toast.error("La URL debe empezar por https://"); return; }
            onSelect({ url: url.trim() }); onClose();
          }}>
            <div className="relative flex-1">
              <Link2 size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input className="pl-8" placeholder="O pega la URL de una imagen (https://…)" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>
            <Button small type="submit">Usar URL</Button>
          </form>
        )}
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin"
          onDragOver={(e) => { if (isAdmin) e.preventDefault(); }}
          onDrop={(e) => { if (!isAdmin) return; e.preventDefault(); void onFiles(e.dataTransfer.files); }}>
          {list.isLoading ? <Loading /> : list.isError ? <p className="text-sm text-danger">No se pudo cargar la biblioteca.</p>
            : !list.data?.items.length ? <EmptyState title="Aún no hay imágenes" body={isAdmin ? "Sube una imagen o arrástrala aquí (PNG, JPG, GIF o WEBP, máx. 5 MB)." : "Un administrador puede subir imágenes."} />
            : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {list.data.items.map((m) => (
                  <div key={m.id} className="group relative rounded-md border border-line overflow-hidden bg-canvas">
                    <button type="button" className="block w-full aspect-[4/3] bg-surface" title="Usar esta imagen" onClick={() => { onSelect({ url: m.url, filename: m.filename }); onClose(); }}>
                      <img src={m.url} alt={m.filename} loading="lazy" className="w-full h-full object-contain" />
                    </button>
                    <div className="px-2 py-1.5 text-[11px] text-ink-secondary truncate" title={m.filename}>{m.filename} · {fmtSize(m.sizeBytes)}</div>
                    {isAdmin && (
                      <button type="button" className="absolute top-1.5 right-1.5 hidden group-hover:block btn-secondary btn-sm !p-1" title="Eliminar"
                        onClick={() => { if (confirm(`¿Eliminar «${m.filename}»? Los correos o landings que la usen dejarán de mostrarla.`)) remove.mutate(m.id, { onError: (e) => toast.error(e.message) }); }}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
